import React, { useLayoutEffect } from 'react';
import fr from 'facet-rest';
import NoDataTemplate from 'Views/Page/Content/Templates/NoDataTemplate';
import history from 'State/history';
import { advisorPage, clientPage, clientPaths } from 'Consts/Strings';
import { useLayoutContext } from 'Context/LayoutContext';
import { getAPIURL } from 'Consts/sharedUris';
import { makeLinkOpenInFocusMode, openURLInNewTab } from 'Helpers/Navigation'; import { QueryVal } from 'Helpers/Query';
import { TemplateConfig as tc } from './TemplateConfig';
import NoTemplateTemplate from './NoTemplateTemplate';
import { templateMap } from './templates';
import './validateTemplates';

const isFacetRestLink = (link) => {
  return link.url && typeof link.url === 'function';
};

const buildPageLink = (link, urlHome, params = {}, linkParams = {}) => {
  if (!link) {
    return '';
  }
  let pageSelfLink = link;
  if (isFacetRestLink(link)) {
    pageSelfLink = link.url();
  }

  // Append linkParams
  const linkSearchParams = new URLSearchParams(linkParams);

  if (linkSearchParams.size > 0) {
    if (pageSelfLink.includes('?')) {
      pageSelfLink += '&';
    } else {
      pageSelfLink += '?';
    }
    pageSelfLink += linkSearchParams;
  }

  const urlSearchParams = new URLSearchParams({ pageSelfLink, ...params });
  return `${urlHome}?${urlSearchParams.toString()}`;
};

const buildAdvisorLink = (link, params, linkParams) => buildPageLink(link, advisorPage, params, linkParams);
const buildClientLink = (link, params, linkParams) => buildPageLink(link, clientPage, params, linkParams);

export const buildMemberMessagesPageLink = (householdResource, threadSelfLink = undefined) => {
  return buildPageLink(householdResource.relationship(fr.relationships.messagesPage.name).link('related'), clientPaths.messagesPage, { threadSelfLink: threadSelfLink ?? QueryVal('threadSelfLink', '') });
};

const buildPageLinkByUserType = (link, isAgent, params = {}, linkParams = {}) => {
  if (isAgent) {
    return buildAdvisorLink(link, params, linkParams);
  }

  return buildClientLink(link, params, linkParams);
};

const navigateToPage = (pageSelfLink, isAgent, params = {}, replace = false, linkParams) => {
  const pageLink = buildPageLinkByUserType(pageSelfLink, isAgent, params, linkParams);
  if (replace) {
    history.replace(pageLink);
  } else {
    history.push(pageLink);
  }
};

const navigateToPageInNewTab = (pageSelfLink, isAgent, params = {}, linkParams) => {
  const pageLink = buildPageLinkByUserType(pageSelfLink, isAgent, params, linkParams);
  window.open(pageLink, '_blank', 'noreferrer noopener');
};

export const hrefByRelationship = (rel) => {
  if (!rel) {
    return undefined;
  }

  // If this URL is for the API, then we want to use navigateByRelationship to get to it, so we can't have an href.
  const url = rel.link('related').url();
  if (url.startsWith(getAPIURL())) {
    return undefined;
  }
  // This is some non-API URL, for example a mailto: link.
  // We can't use navigateByRelationship on it, because it's not one of our resources (even if it was, our interceptor would block the request).
  // As a result, we must use this URL as the href
  return url;
};

export const navigateByRelationship = async (rel, loadModalCollectionByLink, loadPageContentLink, clearModalCollection, { forceFocusMode = false } = {}) => {
  if (!rel) {
    return undefined;
  }

  // If this ends up being an href, then let it just be that and not have any special onClick for navigation
  if (hrefByRelationship(rel)) {
    return undefined;
  }

  if (rel.meta('modal') === 'true') {
    return loadModalCollectionByLink(rel.link('related').url());
  }

  if (rel.meta('openInNewTab')) {
    const newLocation = new URL(window.location.toString());
    newLocation.searchParams.delete('modalSelfLink');
    newLocation.searchParams.set('pageSelfLink', rel.link('related').url());
    if (forceFocusMode) {
      makeLinkOpenInFocusMode(newLocation);
    }
    openURLInNewTab(newLocation.toString(), '');
    return undefined;
  }
  await loadPageContentLink(rel);
  await clearModalCollection();
  return undefined;
};

/**
 * navigateToClientPage Deprecated - use navigateToPage
 * @deprecated
 */
const navigateToClientPage = (pageSelfLink, params = {}, replace = false) => {
  navigateToPage(pageSelfLink, false, params, replace);
};

const noDataTemplateConfig = tc.build(NoDataTemplate).deprecatedDebouncePageLoads().hideNavigation();
const noTemplateTemplateConfig = tc.build(NoTemplateTemplate).deprecatedDebouncePageLoads().hideNavigation();

function getTemplateConfig(pageContentCollection) {
  // If you have no data then return that template
  if (!pageContentCollection) {
    return noDataTemplateConfig;
  }

  // Look for a template and return if found
  const templateConfigFromMap = templateMap[pageContentCollection.meta().type()]?.page;
  if (templateConfigFromMap) {
    return templateConfigFromMap;
  }

  // Return that we couldn't find a template
  return noTemplateTemplateConfig;
}

export default function usePageTemplate(pageContentCollection, pageContentResource, setDeprecatedDebouncePageLoads) {
  const { setNavigationVisible, setHideBottomNavigation } = useLayoutContext();
  const hasNavigationRelationship = pageContentResource?.withRelationship(fr.relationships.navigation.name, () => true, () => false);
  const hasHiddenNavigationRelationship = pageContentResource?.withRelationship(fr.relationships.hiddenNavigation.name, () => true, () => false);

  const templateConfig = getTemplateConfig(pageContentCollection);
  setDeprecatedDebouncePageLoads(templateConfig.isUsingDeprecatedDebouncePageLoads());

  // Figure out what the component should be. Wrap in withSiteNavigation if not behind the feature toggle.
  const Component = React.useMemo(() => {
    return templateConfig.component();
  }, [templateConfig]);

  // Whenever we encounter a new template update navigation
  React.useEffect(() => {
    // If the template is built to take control over layout context, then anything we do in this use effect would cause issues for it - let the template do its own thing
    if (templateConfig.checkIfControlsLayoutContext()) {
      return;
    }
    // We may have a relationship indicating to hide the navigation -- that will take precedent if it exists
    if (hasHiddenNavigationRelationship) {
      setNavigationVisible(false);
      setHideBottomNavigation(true);
      return;
    }
    setNavigationVisible(!templateConfig.isNavigationHidden() && !hasNavigationRelationship);
    setHideBottomNavigation(templateConfig.isMobileNavigationHidden());
  }, [setNavigationVisible, setHideBottomNavigation, templateConfig, hasNavigationRelationship, hasHiddenNavigationRelationship]);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [pageContentCollection?.meta()?.type(), pageContentResource?.id()]);


  return Component;
}

export {
  buildPageLink,
  buildAdvisorLink,
  buildClientLink,
  buildPageLinkByUserType,
  navigateToPage,
  navigateToPageInNewTab,
  navigateToClientPage,
  templateMap,
};
